import Container from "../../../components/element/container";
import {
  getInspectionSections,
  getInspectionTotals,
  getInspectionGrades,
  getFindingMedia,
} from "../../../helpers/inspection.report";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MDBModalBody } from "mdb-react-ui-kit";
import { closeReportModal } from "../../../redux/reducer/modal.reducer";
import { openFullSizeGallery, setFullSizeGallery } from "../../../redux/reducer/common.reducer";
import FullModal from "../../../components/modal/full.modal";
import Col from "../../../components/element/col";
import Row from "../../../components/element/row";
import Button from "../../../components/element/button";
import Icon from "../../../components/element/icon";
import Image from "../../../components/element/image";
import CommonInspectionSummary from "../../../components/common/common.inspection.summary";
import { downloadInspectionReport } from "../../../helpers/download.inspection.report";
import { showToast } from "../../../helpers/show.toast";
import { Swiper, SwiperSlide } from "swiper/react";

function ReportFinding({ item }) {
  const dispatch = useDispatch();
  const note = item?.remark || item?.note || item?.description || "";
  const action = item?.action || "";
  const media = getFindingMedia(item);

  if (!action && !note && media.length === 0) return null;

  const openPhoto = (index) => {
    const slides = media.map((photo) => ({
      src: photo.url,
      title: action || item.label,
      description: note,
    }));
    dispatch(setFullSizeGallery([...slides.slice(index), ...slides.slice(0, index)]));
    dispatch(openFullSizeGallery());
  };

  return (
    <Container tag="div" className="report-finding">
      {action && <p className="report-finding-action"><span>Recommended action</span><strong>{action}</strong></p>}
      {note && (
        <Container tag="div" className="report-finding-note"><span>Inspector’s remark</span><p>{note}</p></Container>
      )}
      {media.length > 0 && (
          <Container tag="div" className="report-finding-media" role="group" aria-label={`${item.label || "Inspection"} photos`}>
            {media.map(({ url }, index) => (
              <button
                type="button"
                className="report-finding-photo"
                key={url}
                aria-label={`View ${item.label || "inspection"} photo ${index + 1} fullscreen`}
                onClick={() => openPhoto(index)}
              >
              <Image
                src={url}
                alt={note || "Inspection finding"}
                className="_img report-finding-image"
              />
              <span className="report-finding-zoom" aria-hidden="true">↗</span>
              </button>
            ))}
          </Container>
      )}
    </Container>
  );
}

export default function ReportModal() {
  const swiperRef = useRef(null);
  const sectionRefs = useRef({});
  const reportLayoutRef = useRef(null);
  const scrollTargetRef = useRef(null);
  const scrollTimerRef = useRef(null);
  const vehicleDetailRef = useRef(null);
  const dispatch = useDispatch();
  const t = (key) => key;

  const { productInfo } = useSelector((state) => state.product);
  const { isOpenReportModal, reportFocus } = useSelector(
    (state) => state.modal,
  );

  const inspectInfo = productInfo?.inspection_report || {};

  const [activeTab, setActiveTab] = useState("Overview");
  const [vehicleDetailHeight, setVehicleDetailHeight] = useState(0);

  const [showAllDetails, setShowAllDetails] = useState(false);
  const [filter, setFilter] = useState("all");
  const [downloadingReport, setDownloadingReport] = useState(false);

  const handleCloseReport = () => {
    dispatch(closeReportModal());
  };

  const reportPointList = useMemo(
    () => getInspectionSections(inspectInfo), [inspectInfo],
  );
  const visibleReports = useMemo(
    () => reportPointList.map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        if (filter === "passed") return item?.status === "yes";
        if (filter === "identified") return item?.status === "no";
        return true;
      }),
    })).filter((section) => section.items.length > 0),
    [reportPointList, filter],
  );
  const tabList = useMemo(
    () => ["Overview", ...visibleReports.map((section) => section.title)],
    [visibleReports],
  );

  const hasDetailValue = (value) =>
    value !== undefined && value !== null && value !== "";
  const normalizeDetailKey = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  const specificationValue = (...keys) => {
    for (const source of [inspectInfo, productInfo?.properties, productInfo]) {
      for (const key of keys) {
        if (hasDetailValue(source?.[key])) return source[key];
      }
      if (Array.isArray(source?.specifications)) {
        for (const key of keys) {
          const specification = source.specifications.find(
            (item) =>
              normalizeDetailKey(
                item?.label || item?.name || item?.title || item?.key,
              ) === normalizeDetailKey(key),
          );
          const value = specification?.value ?? specification?.description;
          if (hasDetailValue(value)) return value;
        }
      }
    }
    return "-";
  };
  const formatDetailValue = ({ value, unit }) => {
    if (!unit || value === "-") return value;
    // Values may already include their unit (for example, "19inch").
    return String(value).trim().toLowerCase().endsWith(unit.toLowerCase())
      ? value
      : `${value} ${unit}`;
  };

  const vehicleDetailList = [
    {
      label: "Brand",
      value: inspectInfo?.brand_name,
      icon: "famicons:car-sport-sharp",
      icon_width: 19,
      icon_height: 18,
    },
    {
      label: "Model",
      value: inspectInfo?.model_name,
      icon: "boxicons:layout-search",
    },
    {
      label: "Variant",
      value: inspectInfo?.variant,
      icon: "mdi:steering",
    },
    {
      label: "Car Plate No",
      value: inspectInfo?.car_plate_no,
      icon: "fluent:text-number-format-20-filled",
      // icon_width: 20,
      // icon_height: 20,
    },
    {
      label: "Manufactured Year",
      value: inspectInfo?.year,
      icon: "uil:calender",
      icon_width: 19,
      icon_height: 17.5,
    },
    {
      label: "Exterior Color",
      value: specificationValue("exterior_color", "body_color", "color"),
      icon: "mdi:palette-outline",
    },
    {
      label: "Interior Color",
      value: specificationValue("interior_color"),
      icon: "lsicon:decorate-outline",
      icon_width: 20,
      icon_height: 20,
    },
    {
      label: "Chassis No",
      value: inspectInfo?.chassis_no
        ? inspectInfo.chassis_no.length <= 3
          ? inspectInfo.chassis_no
          : `${inspectInfo.chassis_no.slice(0, 3)}${"*".repeat(
              inspectInfo.chassis_no.length - 3,
            )}`
        : null,
      icon: "jam:id-card",
      icon_width: 20,
      icon_height: 20,
    },
    {
      label: "Mileage At Inspection",
      value: specificationValue("mileage_at_inspection", "mileage"),
      unit: "km",
      icon: "bx:tachometer",
      icon_width: 19.5,
      icon_height: 19.5,
    },
    {
      label: "Fuel Type",
      value: inspectInfo?.fuel_type,
      icon: "mdi:fuel-pump",
      icon_width: 18.5,
      icon_height: 18.5,
    },
    {
      label: "Engine Capacity",
      value: inspectInfo?.engine_cc,
      unit: "cc",
      icon: "mdi:engine-outline",
      icon_width: 18.5,
      icon_height: 18.5,
    },
    {
      label: "Transmission",
      value: specificationValue(
        "transmission",
        "gear_type",
        "transmission_type",
      ),
      icon: "gravity-ui:gear",
      icon_width: 17,
      icon_height: 17,
    },
    {
      label: "Seats",
      value: specificationValue("seats", "seat_capacity", "seat_count"),
      icon: "mdi:car-seat",
      icon_width: 19,
      icon_height: 19.5,
    },
    {
      label: "Door Style",
      value: inspectInfo?.door_style,
      icon: "mdi:car-door",
      icon_width: 17.5,
      icon_height: 17.5,
    },
    {
      label: "Rim Size",
      value: specificationValue("rim_size"),
      unit: "inch",
      icon: "mdi:wheel",
      icon_width: 18,
      icon_height: 18,
    },
    {
      label: "Insurance Claim",
      value: specificationValue(
        "insurance_claim",
        "insurance_claims",
        "claim_count",
      ),
      icon: "mdi:shield-check-outline",
    },
    {
      label: "Drive Train",
      value: specificationValue("drive_train", "drivetrain"),
      icon: "mdi:car-shift-pattern",
    },
    {
      label: "Engine",
      value: specificationValue("engine", "engine_type"),
      icon: "mdi:engine-outline",
    },
    {
      label: "Battery Type",
      value: specificationValue("battery_type"),
      icon: "mdi:battery-outline",
    },
    {
      label: "Battery Capacity",
      value: specificationValue("battery_capacity_kwh", "battery_capacity"),
      unit: "kWh",
      icon: "mdi:battery-high",
    },
    {
      label: "Battery SOH",
      value: specificationValue("battery_soh", "battery_health"),
      unit: "%",
      icon: "mdi:battery-heart-outline",
    },
    {
      label: "Fast Charge",
      value: specificationValue("fast_charge"),
      icon: "mdi:battery-charging",
    },
    {
      label: "Drive Range",
      value: specificationValue("drive_range", "driving_range"),
      unit: "km",
      icon: "mdi:map-marker-distance",
    },
    {
      label: "Car Location",
      value: specificationValue("car_location", "location"),
      icon: "mdi:map-marker-outline",
    },
  ];

  const { passed, identified: issues, total } = getInspectionTotals(inspectInfo);
  const conditionGrades = getInspectionGrades(inspectInfo);
  const sectionIssues = Object.fromEntries(reportPointList.map((section) => [
    section.title, section.items.filter((item) => item?.status === "no").length,
  ]));
  const filterList = [
    ["all", "All", total],
    ["passed", "Passed", passed],
    ["identified", "Identified", issues],
  ];

  const focusGrade = reportFocus?.grade || null;
  // Builds the same PDF the product page offers, rather than linking to
  // whatever file happens to be attached to the order.
  const handleDownloadReport = async () => {
    if (downloadingReport) return;
    setDownloadingReport(true);

    try {
      const { failedPhotos } = await downloadInspectionReport(productInfo);

      if (failedPhotos) {
        showToast(
          "Report downloaded",
          `${failedPhotos} photo(s) could not be loaded and are marked in the report.`,
          "warning",
        );
      }
    } catch (error) {
      showToast(
        "Unable to download report",
        error.message || "Please try again.",
        "error",
      );
    } finally {
      setDownloadingReport(false);
    }
  };

  useEffect(() => {
    if (!isOpenReportModal) return;

    setFilter(
      ["passed", "identified"].includes(reportFocus?.filter)
        ? reportFocus.filter : "all",
    );
    setActiveTab("Overview");
  }, [isOpenReportModal, reportFocus]);

  useEffect(() => {
    const idx = tabList.indexOf(activeTab);
    if (idx >= 0) swiperRef.current?.slideTo(idx, 200);
  }, [activeTab, tabList]);

  const cancelTabScroll = () => {
    scrollTargetRef.current = null;
    window.clearTimeout(scrollTimerRef.current);
  };

  useEffect(() => () => window.clearTimeout(scrollTimerRef.current), []);

  useEffect(() => {
    scrollTargetRef.current = null;
    window.clearTimeout(scrollTimerRef.current);
    setActiveTab("Overview");
  }, [filter]);

  const selectReportTab = (tab) => {
    const container = reportLayoutRef.current?.closest(".report-modal-body");
    if (!container) return;
    const section = sectionRefs.current[tab];
    const tabsHeight = container.querySelector(".report-mobile-navigation")
      ?.getBoundingClientRect().height || 0;
    const destination = section
      ? container.scrollTop + section.getBoundingClientRect().top -
        container.getBoundingClientRect().top - tabsHeight - 16
      : 0;
    const top = Math.max(0, Math.min(destination, container.scrollHeight - container.clientHeight));
    cancelTabScroll();
    scrollTargetRef.current = top;
    setActiveTab(tab);
    // Scroll events between here and the destination must not select the
    // intervening tabs. Touch/wheel input immediately returns control to users.
    scrollTimerRef.current = window.setTimeout(cancelTabScroll, 1500);
    container.scrollTo({
      top,
      behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth",
    });
  };

  const handleReportScroll = (event) => {
    const container = event.currentTarget;
    if (scrollTargetRef.current !== null) {
      if (Math.abs(container.scrollTop - scrollTargetRef.current) < 2) cancelTabScroll();
      return;
    }
    const tabsHeight =
      container
        .querySelector(".report-mobile-navigation")
        ?.getBoundingClientRect().height || 0;
    const top = container.getBoundingClientRect().top + tabsHeight + 24;
    let current = "Overview";
    const sections = tabList.slice(1)
      .map((title) => [title, sectionRefs.current[title]])
      .filter(([, el]) => el);
    sections.forEach(([title, el]) => {
      if (el.getBoundingClientRect().top <= top) current = title;
    });
    if (
      container.scrollTop > 0 &&
      container.scrollHeight - container.scrollTop - container.clientHeight < 4
    ) {
      current = sections.at(-1)?.[0] || current;
    }
    setActiveTab(current);
  };

  useEffect(() => {
    if (vehicleDetailRef.current) {
      setVehicleDetailHeight(vehicleDetailRef.current.scrollHeight);
    }
  }, [vehicleDetailList]);

  return (
    <FullModal
      staticBackdrop={true}
      show={isOpenReportModal}
      close={handleCloseReport}
      screenSize="xl"
      className="report-modal"
      content={
        <MDBModalBody
          className="report-modal-body"
          onScroll={handleReportScroll}
          onWheel={cancelTabScroll}
          onTouchStart={cancelTabScroll}
        >
          <div className="report-layout" ref={reportLayoutRef}>
            <Container tag="div" className="report-mobile-navigation">
              <Col className="_col sticky-tab">
                <Swiper
                  onSwiper={(swiper) => (swiperRef.current = swiper)}
                  spaceBetween={10}
                  slidesPerView="auto"
                  grabCursor={true}
                  className="w-100"
                >
                  {tabList.map((tab) => (
                    <SwiperSlide
                      key={tab}
                      onClick={() => selectReportTab(tab)}
                    >
                      <Col
                        key={tab}
                        className={`_col tab-item ${
                          activeTab === tab ? "active" : ""
                        } pointer`}
                      >
                        <p>{t(tab)}</p>
                      </Col>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </Col>
            </Container>
            <aside className="report-sidebar">
              <nav className="report-sidebar-nav">
                {tabList.map((tab) => (
                  <button
                    type="button"
                    className={`report-sidebar-item ${
                      activeTab === tab ? "active" : ""
                    }`}
                    key={tab}
                    onClick={() => selectReportTab(tab)}
                  >
                    <span>{t(tab)}</span>
                    {sectionIssues[tab] > 0 && (
                      <em className="report-sidebar-badge">
                        {sectionIssues[tab]}
                      </em>
                    )}
                  </button>
                ))}
              </nav>

              <button
                type="button"
                className="report-sidebar-download"
                onClick={handleDownloadReport}
                disabled={downloadingReport}
              >
                <Icon icon="solar:download-minimalistic-linear" />
                {downloadingReport
                  ? t("Preparing report...")
                  : t("Download Report")}
              </button>
            </aside>

            <Col className="_col report-info">
              <Col className="flex-col gap-3 px-4">
                <Container tag="div" className="report-heading">
                  <button
                    type="button"
                    className="report-back"
                    onClick={handleCloseReport}
                    aria-label={t("Back")}
                  >
                    <Icon icon="ep:back" />
                  </button>
                  <h1 className="_label report-title">
                    {total}-Point&nbsp;
                    <span>{t("Inspection Report")}</span>
                  </h1>
                </Container>

                <Row className="flex gap-3 items-center">
                  <Row className="flex gap-2 items-end">
                    <Icon
                      icon="solar:calendar-linear"
                      className="_svg header-icon"
                    />
                    <p className="_label report-date">
                      {inspectInfo.inspection_date}
                    </p>
                  </Row>
                  <Col className="_col detail-divider" />
                  <Row className="flex gap-2 items-end">
                    <Icon
                      icon="solar:user-linear"
                      className="_svg header-icon"
                    />
                    <p className="_label inspector-name">
                      {t("Inspector")}&nbsp;
                      <span>{inspectInfo.technician_name}</span>
                    </p>
                  </Row>
                </Row>
              </Col>

              <Col className="flex-col gap-3 px-3">
                <CommonInspectionSummary
                  t={t}
                  grade={inspectInfo?.inspection_grade}
                  grades={conditionGrades}
                  workshop={
                    inspectInfo?.workshop_name ||
                    inspectInfo?.technician_name ||
                    "DHub"
                  }
                  pointTotal={total}
                  reference={
                    inspectInfo?.reference_no ||
                    inspectInfo?.inspection_id ||
                    "-"
                  }
                  passed={passed}
                  identified={issues}
                  focusGrade={focusGrade}
                  onSelectTally={(key) => setFilter(key)}
                />
              </Col>

              <Col className="flex-col gap-3 px-3">
                <h3 className="_label card-title">
                  {t("Full Vehicle Details")}
                </h3>
                <Col>
                  <article
                    ref={vehicleDetailRef}
                    className={`_col vehicle-list ${
                      !showAllDetails && vehicleDetailHeight > 260
                        ? "collapsed"
                        : ""
                    }`}
                    style={{
                      maxHeight: showAllDetails ? vehicleDetailHeight : 200,
                      transition: "max-height 0.35s ease, opacity 0.3s ease",
                    }}
                  >
                    {vehicleDetailList.map(
                      (detail) =>
                        hasDetailValue(detail.value) && (
                          <Row className="_row vehicle-item" key={detail.label}>
                            <Row className="flex gap-2 items-center">
                              <Icon
                                width={detail.icon_width || 18}
                                height={detail.icon_height || 18}
                                icon={detail.icon}
                                className="_svg detail-icon"
                              />
                              <p className="_label vehicle-label">
                                {t(detail.label)}
                              </p>
                            </Row>
                            <Col className="_col vehicle-divider" />
                            <h6 className="_label vehicle-value">
                              {formatDetailValue(detail)}
                            </h6>
                          </Row>
                        ),
                    )}
                  </article>
                  {vehicleDetailHeight > 260 && (
                    <Button
                      className="_button show-hide compact"
                      onClick={() => setShowAllDetails((prev) => !prev)}
                    >
                      {t(showAllDetails ? "Show Less" : "Show More")}
                      <Icon
                        icon={
                          showAllDetails
                            ? "iconamoon:arrow-up-2-light"
                            : "iconamoon:arrow-down-2-light"
                        }
                        className="_svg action-arrow"
                      />
                    </Button>
                  )}
                </Col>
              </Col>

              <Row className="_row report-filters">
                {filterList.map(([key, label, count]) => (
                  <button
                    type="button"
                    className={`report-filter ${filter === key ? "active" : ""}`}
                    key={key}
                    onClick={() => setFilter(key)}
                  >
                    {t(label)}
                    <em>{count}</em>
                  </button>
                ))}
              </Row>

              {visibleReports.map((report) => {

                return (
                  <div
                    ref={(el) => (sectionRefs.current[report.title] = el)}
                    data-title={report.title}
                    className="flex-col gap-3 px-3"
                    key={report.key}
                  >
                    <h3 className="_label card-title">{t(report.title)}</h3>
                    <Col className="_col report-list">
                      {report.items.map((item, pointIndex) => (
                        <Col className="_col report-entry" key={pointIndex}>
                          <Row className="_row report-item">
                            <p className="_label report-label">{t(item.label || `Check ${pointIndex + 1}`)}</p>
                            {item.status === "yes" ? (
                              <Icon
                                icon="mingcute:check-circle-fill"
                                className="_svg report-icon success"
                              />
                            ) : item.status === "no" ? (
                              <Icon
                                icon="mage:exclamation-circle-fill"
                                className="_svg report-icon warning"
                              />
                            ) : (
                              <Icon
                                icon="flowbite:close-circle-solid"
                                className="_svg report-icon danger"
                              />
                            )}
                          </Row>
                          <ReportFinding item={item} />
                        </Col>
                      ))}
                    </Col>
                  </div>
                );
              })}
            </Col>
          </div>
        </MDBModalBody>
      }
    />
  );
}
